import { DatePipe } from '@angular/common';
import { Component, Injector } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BaseResourceListComponent,
  Coluna,
  DateFormatPipe,
  Exercicio,
  ExercicioService,
  Filtro,
  FormatoExportacao,
  FuncaoService,
  LoginPublico,
  Receita,
  Sistemas,
  TransparenciaParametro
} from 'eddydata-lib';
import { TreeNode } from 'primeng/api';
import { TransparenciaParametroService } from 'projects/transparencia/src/app/transparencia-parametro/service/transparencia-parametro.service';
import { Observable } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { RecebimentoService } from '../recebimento/service/recebimento.service';
import {
  montarArvoreReceitaPrevistoArrecadado,
  normalizarPeriodo,
  obterLinhasVisiveisExportacao,
  ReceitaPrevistoArrecadadoLinha
} from './receita-nivel-previsto-arrecadado.tree';

@Component({
  selector: 'app-receita-nivel-previsto-arrecadado',
  templateUrl: './receita-nivel-previsto-arrecadado.component.html',
  styleUrls: ['./receita-nivel-previsto-arrecadado.component.scss']
})
export class ReceitaNivelPrevistoArrecadadoComponent extends BaseResourceListComponent<Receita, LoginPublico> {

  public parametro: TransparenciaParametro;
  public sistema: Sistemas;
  public exercicio: Exercicio;
  public listaExercicios: Array<Exercicio>;
  public mesInicial = 1;
  public mesFinal = 12;
  public carregando = false;
  public datepipe: DatePipe;
  public arvoreReceitas: TreeNode[] = [];
  public skeletonRows: TreeNode[] = [
    { data: {} }, { data: {} }, { data: {} }, { data: {} },
    { data: {} }, { data: {} }, { data: {} }, { data: {} },
    { data: {} }, { data: {} }, { data: {} }, { data: {} },
    { data: {} }, { data: {} }, { data: {} }, { data: {} },
    { data: {} }, { data: {} }, { data: {} }, { data: {} },
  ];
  public meses = [
    { id: 1, nome: 'Janeiro' },
    { id: 2, nome: 'Fevereiro' },
    { id: 3, nome: 'Março' },
    { id: 4, nome: 'Abril' },
    { id: 5, nome: 'Maio' },
    { id: 6, nome: 'Junho' },
    { id: 7, nome: 'Julho' },
    { id: 8, nome: 'Agosto' },
    { id: 9, nome: 'Setembro' },
    { id: 10, nome: 'Outubro' },
    { id: 11, nome: 'Novembro' },
    { id: 12, nome: 'Dezembro' },
  ];

  constructor(
    private route: ActivatedRoute,
    protected injector: Injector,
    private exercicioService: ExercicioService,
    private recebimentoService: RecebimentoService,
    private paramService: TransparenciaParametroService,
    private funcaoService: FuncaoService,
  ) {
    super(recebimentoService, injector);
    this.datepipe = new DatePipe('pt');
  }

  public preencherGrid(): void {
    this.route.params
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(params => {
        this.sistema = params['sistema'];

        this.exercicioService
          .obterPorAno(+params['ano'], this.login.cidade.id)
          .pipe(takeUntil(this.unsubscribe))
          .subscribe(res => {
            this.exercicio = res.content[0];
            this.carregarGridPorFiltros();
          });
      });
  }

  protected relations(): string {
    return '';
  }

  protected condicoesGrid(): {} {
    return {
      ['exercicio.id']: this.exercicio?.id
    };
  }

  protected ordenacaoGrid(): string[] {
    return ['codigo$ASC'];
  }

  protected filtrosGrid(): Filtro {
    return {};
  }

  protected afterInit(): void {
    this.exercicioService
      .obterTodosOrdenadoPorAno(this.login.cidade.id)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(dados => {
        this.listaExercicios = dados.content;
      });

    this.paramService
      .obterParametro(this.login.orgao.id)
      .subscribe(res => {
        this.parametro = res;
        this.parametro.orgao = this.login.orgao;
      });
  }

  protected acaoRemover(model: Receita): Observable<Receita> {
    return null;
  }

  protected colunasRelatorio(): string[] | Coluna[] {
    return [
      { titulo: 'Receita', coluna: 'receita' },
      { titulo: 'Valor Previsto', coluna: 'previsto', decimais: 2, alignment: 'right' },
      { titulo: 'Valor Arrecadado', coluna: 'arrecadado', decimais: 2, alignment: 'right' },
    ];
  }

  public exportarListagem(formato: FormatoExportacao): void {
    const lista = this.obterListaExportacao();

    if (formato === 'pdf') {
      this.imprimir(
        'RECEITA PREVISTA X ARRECADADA',
        this.login.usuario.nome,
        this.login.usuario.sobrenome,
        this.login.orgao.nome,
        this.login.brasao,
        'portrait',
        'Receita prevista x arrecadada',
        ['*', 'auto', 'auto'],
        lista
      );
      return;
    }

    this.exportar(formato, lista, 'receita-prevista-x-arrecadada');
  }

  public voltar(): void {
    switch (this.sistema) {
      case 'transparencia':
        this.router.navigate(['/transparencia/portal']);
        break;
      default:
        this.sair();
        break;
    }
  }

  public onChangeExercicio(exercicio: Exercicio): void {
    if (!exercicio?.id) {
      return;
    }

    this.exercicio = exercicio;
    this.carregarGridPorFiltros();
  }

  public onChangePeriodo(): void {
    const periodo = normalizarPeriodo(+this.mesInicial, +this.mesFinal);
    this.mesInicial = periodo.mesInicial;
    this.mesFinal = periodo.mesFinal;
    this.carregarGridPorFiltros();
  }

  public obterListaExportacao(): ReceitaPrevistoArrecadadoLinha[] {
    return obterLinhasVisiveisExportacao(this.arvoreReceitas);
  }

  private carregarGridPorFiltros(): void {
    if (!this.exercicio?.id) {
      toastr.warning('Informe o exercício');
      return;
    }
    if (!this.mesInicial) {
      toastr.warning('Informe o mês inicial');
      return;
    }
    if (!this.mesFinal) {
      toastr.warning('Informe o mês final');
      return;
    }

    this.carregando = true;
    this.arvoreReceitas = [];

    this.carregarGridPorExercicio();
  }

  private carregarGridPorExercicio(): void {
    this.recebimentoService
      .arrecadadoPrevistoPorNivel(
        this.login.orgao.id,
        this.exercicio.id,
        this.mesInicial,
        this.mesFinal
      ).pipe(
        finalize(() => this.carregando = false),
        takeUntil(this.unsubscribe)
      ).subscribe(lista => {
        this.arvoreReceitas = montarArvoreReceitaPrevistoArrecadado(lista);
      });
  }

  public redirecionar(codigo: string) {
    const data1 = new DateFormatPipe().transform(this.datepipe.transform(new Date(`${this.mesInicial}/01/${this.exercicio.ano}`), 'yyyy-MM-dd'), []);
    const data2 = new DateFormatPipe().transform(this.datepipe.transform(new Date(`${this.mesFinal}/${this.funcaoService.ultimoDiaMes(this.mesFinal)}/${this.exercicio.ano}`), 'yyyy-MM-dd'), []);
    const dt1 = this.datepipe.transform(data1, 'yyyy-MM-dd', 'GMT');
    const dt2 = this.datepipe.transform(data2, 'yyyy-MM-dd', 'GMT');

    switch (this.sistema) {
      case 'transparencia':
        const rota = ['/transparencia/receitas-orcamentarias/pesquisa/todos/1', dt1, dt2];
        sessionStorage.setItem(rota.join('/') + '_filtro', codigo)
        this.router.navigate(rota);
        break;
      case 'indicador-gestao':
      case 'controle-interno':
      case 'contabil':
        sessionStorage.setItem('/recebimentos_data1', dt1);
        sessionStorage.setItem('/recebimentos_data2', dt2);
        sessionStorage.setItem('/recebimentos_filtro', codigo);
        this.router.navigate(['/recebimentos-orcamentarios']);
        break;
      default:
        this.sair();
        break;
    }
  }
}
